import { Fragment, type FunctionComponent, useCallback } from 'react';
import { FormattedDate, FormattedNumber, FormattedTime } from 'react-intl';

import { Button, ButtonSize, Rounding } from '../../../shared/components/button/button';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Icon } from '../../../shared/components/icon/icon';
import { IconHover } from '../../../shared/components/icon-hover/icon-hover';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { slugify } from '../../../shared/utils/strings';
import type { BudgetLimitsTransactionTypes } from '../../limits.interface';
import { IActivationStatus, ICreatorName } from '../../queries/budget-limit.graphql';
import type {
    ILimitsMaxWagerResponse,
    IBudgetLimitsTransactionResponse,
} from '../../queries/budget-limit.graphql';
import { LimitStatusHistory } from '../limit-status-history/limit-status-history';

import styles from './budget-limit-history-table.module.scss';

const style = createStyleHelper(styles, 'budget-limit-history-table');

export interface IBudgetLimitHistoryTableProps {
    history: IBudgetLimitsTransactionResponse[] | ILimitsMaxWagerResponse[];
    onLimitCancel: (id: string, type?: BudgetLimitsTransactionTypes) => void;
}

function CancelLimit({
    onLimitCancel,
    id,
    disabled,
    isPendingCancellation,
    type,
}: {
    id: string;
    onLimitCancel: (id: string, type?: BudgetLimitsTransactionTypes) => void;
    disabled: boolean;
    isPendingCancellation: boolean;
    type?: BudgetLimitsTransactionTypes;
}) {
    const onClick = useCallback(() => onLimitCancel(id, type), [id, onLimitCancel, type]);
    return (
        <Button
            className={style('cancel')}
            size={ButtonSize.MEDIUM}
            rounding={Rounding.SMALL}
            warning
            onClick={onClick}
            disabled={disabled}
        >
            <FormattedMessage
                id={
                    isPendingCancellation
                        ? 'budget-limit-history-table.revoke-limit.label'
                        : 'budget-limit-history-table.cancel-limit.label'
                }
                defaultMessage={isPendingCancellation ? 'Revoke' : 'Cancel Limit'}
            />
        </Button>
    );
}

export const BudgetLimitHistoryTable: FunctionComponent<IBudgetLimitHistoryTableProps> = ({
    history,
    onLimitCancel,
}) => {
    const isDesktop = useDesktop();

    if (!history || history.length === 0) {
        return (
            <div className={style('no-data')}>
                <FormattedMessage
                    id="budget-limit-history.no-data-to-display"
                    defaultMessage="No data to display"
                />
            </div>
        );
    }

    return (
        <table className={style()}>
            <thead>
                <tr className={style('headers')}>
                    <th className={style('header', ['added'])}>
                        <FormattedMessage
                            id="budget-limit-history.heading.added"
                            defaultMessage="Added"
                        />
                    </th>
                    <th className={style('header', ['type'])}>
                        <FormattedMessage
                            id="budget-limit-history.heading.type"
                            defaultMessage="Type"
                        />
                    </th>
                    <th className={style('header', ['creator'])}>
                        <FormattedMessage
                            id="budget-limit-history.heading.creator"
                            defaultMessage="Creator"
                        />
                    </th>
                    <th className={style('header', ['product'])}>
                        <FormattedMessage
                            id="budget-limit-history.heading.product"
                            defaultMessage="Product"
                        />
                    </th>
                    <th className={style('header', ['limit'])}>
                        <FormattedMessage
                            id="budget-limit-history.heading.limit"
                            defaultMessage="Limit"
                        />
                    </th>
                    <th className={style('header', ['status'])}>
                        <FormattedMessage
                            id="budget-limit-history.heading.status"
                            defaultMessage="Status"
                        />
                    </th>
                    {isDesktop ? (
                        <th className={style('header', ['action'])}>
                            <FormattedMessage
                                id="budget-limit-history.heading.action"
                                defaultMessage="Action"
                            />
                        </th>
                    ) : null}
                    {isDesktop ? <th className={style('header')} /> : null}
                </tr>
            </thead>
            <tbody>
                {history.map(
                    ({
                        id,
                        createdOn,
                        amount,
                        currencyCode,
                        isCancellable,
                        activationStatus,
                        isPendingCancellation,
                        creator,
                        ...otherInformation
                    }) => (
                        <Fragment key={id}>
                            <tr
                                key={`${id}-info`}
                                className={style('row', { 'divide-x': isDesktop })}
                            >
                                <td className={style('item', ['added'])}>
                                    <FormattedDate value={createdOn ? createdOn : ''} />
                                    <br />
                                    <FormattedTime
                                        value={createdOn ? createdOn : ''}
                                        timeStyle="short"
                                    />
                                </td>
                                <td className={style('item', ['type'])}>
                                    {'type' in otherInformation && 'period' in otherInformation ? (
                                        <>
                                            <FormattedMessage
                                                id={`budget-limit-history.period.${slugify(
                                                    otherInformation.period
                                                        ? otherInformation.period
                                                        : '',
                                                )}.name`}
                                            />
                                            <wbr />{' '}
                                            <FormattedMessage
                                                id={`budget-limit-history.limit-type.${slugify(
                                                    otherInformation.type
                                                        ? otherInformation.type
                                                        : '',
                                                )}-limit.name`}
                                            />
                                        </>
                                    ) : (
                                        <FormattedMessage
                                            id="budget-limit-history.limit-type.max-wager"
                                            defaultMessage="Max Wager"
                                        />
                                    )}
                                </td>
                                <td className={style('item', ['creator'])}>
                                    <FormattedMessage
                                        id={`budget-limit-history.creator.${slugify(
                                            creator ? creator : '',
                                        )}`}
                                        defaultMessage={creator ? creator : ''}
                                    />
                                </td>
                                <td className={style('item', ['product'])}>
                                    {'mediatorType' in otherInformation ? (
                                        <FormattedMessage
                                            id={`budget-limit-history.mediatortype.${slugify(
                                                otherInformation.mediatorType
                                                    ? otherInformation.mediatorType
                                                    : '',
                                            )}`}
                                            defaultMessage={
                                                otherInformation.mediatorType
                                                    ? otherInformation.mediatorType
                                                    : ''
                                            }
                                        />
                                    ) : (
                                        <div>
                                            <FormattedMessage
                                                id="budget-limit-history.mediatortype.casino"
                                                defaultMessage="Casino"
                                            />
                                        </div>
                                    )}
                                </td>
                                <td className={style('item', ['limit'])}>
                                    <FormattedNumber
                                        currency={currencyCode ? currencyCode : ''}
                                        value={amount ? amount : 0}
                                        style="currency"
                                    />
                                </td>
                                <td
                                    className={style('item', [
                                        `status-${slugify(
                                            activationStatus ? activationStatus : '',
                                        )}`,
                                    ])}
                                >
                                    <LimitStatusHistory
                                        activationStatus={
                                            activationStatus ?? IActivationStatus.Active
                                        }
                                        isPendingCancellation={isPendingCancellation ?? false}
                                    />
                                </td>
                                {isDesktop ? (
                                    <td className={style('item', ['action'])}>
                                        {isCancellable ? (
                                            <CancelLimit
                                                id={id ? id : ''}
                                                onLimitCancel={onLimitCancel}
                                                isPendingCancellation={!!isPendingCancellation}
                                                disabled={creator !== ICreatorName.User}
                                                type={
                                                    'type' in otherInformation
                                                        ? (otherInformation.type as BudgetLimitsTransactionTypes)
                                                        : undefined
                                                }
                                            />
                                        ) : null}
                                    </td>
                                ) : null}
                                {isDesktop && creator !== ICreatorName.User ? (
                                    <td className={style('item')}>
                                        <IconHover
                                            openToTheLeft
                                            hoverItem={<Icon name={'info-circle'} />}
                                            infoDisplay={
                                                <FormattedMessage
                                                    id={
                                                        'budget-limit-history.limit-created-by.message.info'
                                                    }
                                                    defaultMessage={
                                                        'This limit was not created by you (the user) and therefore cannot be deleted.'
                                                    }
                                                />
                                            }
                                        />
                                    </td>
                                ) : null}
                            </tr>

                            {!isDesktop ? (
                                <tr
                                    key={`${id}-cancel`}
                                    className={style('row', { 'divide-x': !isDesktop })}
                                >
                                    {isCancellable ? (
                                        <td className={style('item', ['action'])} colSpan={5}>
                                            <CancelLimit
                                                id={id ? id : ''}
                                                onLimitCancel={onLimitCancel}
                                                isPendingCancellation={!!isPendingCancellation}
                                                disabled={creator !== ICreatorName.User}
                                                type={
                                                    'type' in otherInformation
                                                        ? (otherInformation.type as BudgetLimitsTransactionTypes)
                                                        : undefined
                                                }
                                            />
                                        </td>
                                    ) : null}
                                </tr>
                            ) : null}
                        </Fragment>
                    ),
                )}
            </tbody>
        </table>
    );
};
